import type { OfficeExchangeResponse } from "@/lib/office-exchange/types"

/** Extensions stay raw digits; longer dialpad values are external numbers. */
const EXTENSION_DIAL_MAX_LEN = 6

export type NowCallingTransferKind = "extension" | "ring_group" | "queue"

export type NowCallingTransferRequest = {
  destination_type: "extension" | "ring_group" | "queue" | "external"
  destination_number: string
  destination_id?: string
}

export type NowCallingTransferTarget = NowCallingTransferRequest & {
  id: string
  kind: NowCallingTransferKind
  label: string
  /** Digits a user can type on the dialpad to select this row. */
  displayNumber: string
}

export function transferDestinationsFromOfficeExchange(
  data: OfficeExchangeResponse,
): NowCallingTransferTarget[] {
  const extensions: NowCallingTransferTarget[] = (data.users ?? [])
    .filter((user) => !user.is_self && String(user.extension ?? "").trim())
    .map((user) => {
      const extension = String(user.extension).trim()
      return {
        id: `extension-${user.extension_id || user.id}`,
        kind: "extension",
        label: user.name?.trim() || `Extension ${extension}`,
        displayNumber: extension,
        destination_type: "extension",
        destination_number: `ext_${extension}`,
        destination_id: user.extension_id || undefined,
      }
    })

  const ringGroups: NowCallingTransferTarget[] = (data.ring_groups ?? []).map((group) => {
    const groupNum = String(group.group_num)
    return {
      id: `ring-group-${group.id}`,
      kind: "ring_group",
      label: group.name?.trim() || `Ring group ${groupNum}`,
      displayNumber: groupNum,
      destination_type: "ring_group",
      destination_number: group.transfer_number?.trim() || `group_${groupNum}`,
      destination_id: group.id,
    }
  })

  const queues: NowCallingTransferTarget[] = (data.call_queues ?? []).map((queue) => {
    const groupNum = String(queue.group_num)
    return {
      id: `queue-${queue.id}`,
      kind: "queue",
      label: queue.name?.trim() || queue.queue_name?.trim() || `Queue ${groupNum}`,
      displayNumber: groupNum,
      destination_type: "queue",
      destination_number: queue.transfer_number?.trim() || `group_${groupNum}`,
      destination_id: queue.id,
    }
  })

  return [...extensions, ...ringGroups, ...queues]
}

export function resolveDialpadTransfer(
  digits: string,
  destinations: NowCallingTransferTarget[],
): NowCallingTransferRequest | null {
  const trimmed = digits.trim()
  if (!trimmed) return null

  const match = destinations.find((destination) => destination.displayNumber === trimmed)
  if (match) {
    return {
      destination_type: match.destination_type,
      destination_number: match.destination_number,
      destination_id: match.destination_id,
    }
  }

  if (trimmed.length <= EXTENSION_DIAL_MAX_LEN) {
    return {
      destination_type: "extension",
      destination_number: `ext_${trimmed}`,
    }
  }

  return {
    destination_type: "external",
    destination_number: trimmed,
  }
}
